import React, {ForwardedRef, useMemo, useState} from "react";
import {Box, InputLabel, List, ListItem, TextField, Typography, useTheme} from "@mui/material";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {RegexConstants} from "../../common/constants/regex.constants";
import {useDebounce} from "../../common/hooks/useDebounce";
import CheckedCircle from "../../common/icons/misc/checkedCircle";

type CustomUrlInputProps = TextFieldProps & {
    hint?: boolean;
    errorMessage?: string;
    fullHeight?: boolean;
    readOnly?: boolean;
    optional?: boolean;
};

export const CustomUrlInput = React.forwardRef((props: CustomUrlInputProps, ref: ForwardedRef<CustomUrlInputProps>) => {

    const {
        label,
        id,
        focused,
        error,
        onFocus,
        onBlur,
        onChange,
        errorMessage,
        hint,
        readOnly,
        optional,
        InputProps,
        className,
        ...rest
    } = props;

    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(readOnly ? false : focused);
    const [contains3AlphanumericChars, setContains3AlphanumericChars] = useState<boolean>(false);
    const [containsMax30Chars, setContainsMax30Chars] = useState<boolean>(false);
    const [containsValidCharacters, setContainsValidCharacters] = useState<boolean>(false);

    const handleChange = useDebounce((ev: React.ChangeEvent<HTMLInputElement>) => {
        const value = ev.target.value;

        setContainsValidCharacters(RegexConstants.CUSTOMURL_HAS_VALID_CHARACTERS.test(value));
        setContains3AlphanumericChars(RegexConstants.HAS_MINIMUM_3_ALPHANUMERIC_CHARS.test(value));
        setContainsMax30Chars(RegexConstants.HAS_MAXIMUM_30_CHARS.test(value));

        if (onChange) {
            onChange(ev);
        }
    }, 100)

    const InternalInputProps = useMemo(
        () => ({
            readOnly: readOnly,
            startAdornment: <Typography px={0}>thatsme.baby/</Typography>,
            ...InputProps,
        }),
        [readOnly, InputProps]
    );

    return (
        <Box className={className}>
            {label && (
                <InputLabel error={error} focused={isFocused} className={"input-label"} htmlFor={id} sx={{whiteSpace: "pre-wrap", mb: 0.5}}>
                    {label}
                </InputLabel>
            )}
            <TextField
                inputRef={ref}
                className={"input-field"}
                margin="none"
                error={error}
                required={false}
                fullWidth
                focused={focused}
                onFocus={(ev) => {
                    setIsFocused(!readOnly);
                    onFocus?.(ev);
                }}
                onBlur={(ev) => {
                    setIsFocused(false);
                    onBlur?.(ev);
                }}
                id={id}
                onChange={handleChange}
                type={"text"}
                InputProps={InternalInputProps}
                {...rest}
            />
            {props.errorMessage && (
                <Typography component="div" className="error-message" variant={"body2"}>
                    {errorMessage}
                </Typography>
            )}
            {props.hint && (
                <>
                    <Typography component="div" className={"input-help"} mt={0.5} variant={"body2"}>
                        Your URL must 
                    </Typography>
                    <List sx={{listStyleType: 'disc', paddingX: 2.5, '& .MuiListItem-root': {display: 'list-item'}}} className={"input-help"}>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: containsValidCharacters ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                use only letters, numbers or the following symbols _ -  {containsValidCharacters && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)
                            }
                            </Typography>
                        </ListItem>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: contains3AlphanumericChars ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                contain at least 3 alphanumeric characters {contains3AlphanumericChars && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)}
                            </Typography>
                        </ListItem>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: containsMax30Chars ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                be less than 30 characters {containsMax30Chars && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)}
                            </Typography>
                        </ListItem>
                    </List>
                </>
            )}
        </Box>
    );
});

